import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../UI/Input';
import Select from '../UI/Select';
import Button from '../UI/Button';
import FileUpload from '../UI/FileUpload';
import Map from '../UI/Map';
import { api, Category, Parish } from '../../services/api';
import LoadingSpinner from '../UI/LoadingSpinner';
import ProjectFilesManager from './ProjectFilesManager';
import MilestonesEditor from './MilestonesEditor';
import GoalsEditor from './GoalsEditor';
import { useAppSelector } from '../../hooks/redux';

interface ProjectFormProps {
  initialData?: any;
  onSubmit: (data: any) => Promise<void>;
}

const ProjectForm: React.FC<ProjectFormProps> = ({ initialData, onSubmit }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [parishes, setParishes] = useState<Parish[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingParishes, setIsLoadingParishes] = useState(true);
  const [selectedParishCenter, setSelectedParishCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [projectFiles, setProjectFiles] = useState<any[]>([]);
  const { accessToken } = useAppSelector((state) => state.auth);
  
  // Get projectId from initialData (for editing) or from _id field
  const projectId = initialData?._id || initialData?.id || undefined;
  
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    description: initialData?.description || '',
    category: initialData?.category || '',
    geographicRegion: {
      parish: initialData?.geographicRegion?.parish || '',
      country: initialData?.geographicRegion?.country || 'Antigua and Barbuda',
      coordinates: initialData?.geographicRegion?.coordinates || { lat: 17.0747, lng: -61.7964 },
    },
    milestones: initialData?.milestones || [],
    goals: initialData?.goals || [],
    files: [], // Files are now managed separately via ProjectFile collection
  });

  // Fetch project files when editing
  useEffect(() => {
    const fetchFiles = async () => {
      if (!projectId || !accessToken) return;
      try {
        const files = await api.projectFiles.getProjectFiles(projectId, false, accessToken);
        setProjectFiles(files);
      } catch (error) {
        console.error('Error fetching project files:', error);
      }
    };
    if (projectId) {
      fetchFiles();
    }
  }, [projectId, accessToken]);

  // Load categories and parishes from API
  useEffect(() => {
    const loadData = async () => {
      try {
        const [categoriesData, parishesData] = await Promise.all([
          api.categories.getAll(),
          api.parishes.getAll(),
        ]);
        setCategories(categoriesData);
        setParishes(parishesData);
      } catch (error) {
        console.error('Error loading categories/parishes:', error);
      } finally {
        setIsLoadingCategories(false);
        setIsLoadingParishes(false);
      }
    };
    loadData();
  }, []);

  // Update map center when parish is selected
  useEffect(() => {
    if (formData.geographicRegion.parish) {
      const parish = parishes.find((p) => p.name === formData.geographicRegion.parish || p.slug === formData.geographicRegion.parish);
      if (parish) {
        setSelectedParishCenter(parish.center);
        setFormData((prev) => ({
          ...prev,
          geographicRegion: {
            ...prev.geographicRegion,
            coordinates: parish.center,
          },
        }));
      }
    }
  }, [formData.geographicRegion.parish, parishes]);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedChange = (parent: string, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent as keyof typeof prev],
        [field]: value,
      },
    }));
  };

  const handleMapClick = async (lat: number, lng: number) => {
    // Update coordinates
    handleNestedChange('geographicRegion', 'coordinates', { lat, lng });
    
    // Try to find parish by location
    try {
      const parish = await api.parishes.getByLocation(lat, lng);
      if (parish) {
        handleNestedChange('geographicRegion', 'parish', parish.name);
        setSelectedParishCenter(parish.center);
      }
    } catch (error) {
      console.error('Error finding parish for location:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step < 5) {
      setStep(step + 1);
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting project:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <Input
              label="Project Title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              required
              fullWidth
            />
            <div>
              <label className="block text-sm font-medium text-[var(--color-default-font)] mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-[var(--color-neutral-border)] rounded-md bg-[var(--color-default-background)] text-[var(--color-default-font)] focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-500)]"
                rows={6}
                required
              />
            </div>
            {isLoadingCategories ? (
              <LoadingSpinner size="sm" text="Loading categories..." />
            ) : (
              <Select
                label="Category"
                options={[
                  { value: '', label: 'Select a category' },
                  ...categories.map((cat) => ({
                    value: cat.slug,
                    label: cat.name,
                  })),
                ]}
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                required
                fullWidth
              />
            )}
          </div>
        );

      case 2:
        const mapLat = selectedParishCenter?.lat || formData.geographicRegion.coordinates.lat;
        const mapLng = selectedParishCenter?.lng || formData.geographicRegion.coordinates.lng;
        
        return (
          <div className="space-y-4">
            {isLoadingParishes ? (
              <LoadingSpinner size="sm" text="Loading parishes..." />
            ) : (
              <Select
                label="Parish"
                options={[
                  { value: '', label: 'Select a parish or click on map' },
                  ...parishes.map((parish) => ({
                    value: parish.name,
                    label: parish.name,
                  })),
                ]}
                value={formData.geographicRegion.parish}
                onChange={(e) => handleNestedChange('geographicRegion', 'parish', e.target.value)}
                fullWidth
              />
            )}
            <div>
              <label className="block text-sm font-medium text-[var(--color-default-font)] mb-2">
                Location Map
                <span className="text-xs text-[var(--color-subtext-color)] ml-2">
                  (Click on map to select location - parish will be auto-detected)
                </span>
              </label>
              <Map
                latitude={mapLat}
                longitude={mapLng}
                zoom={11}
                interactive={true}
                onMapClick={handleMapClick}
                onLocationChange={(lat, lng) =>
                  handleNestedChange('geographicRegion', 'coordinates', { lat, lng })
                }
                showClickMarker={true}
                className="h-96"
              />
              {formData.geographicRegion.coordinates && (
                <div className="mt-2 text-sm text-[var(--color-subtext-color)]">
                  Selected: {formData.geographicRegion.coordinates.lat.toFixed(6)}, {formData.geographicRegion.coordinates.lng.toFixed(6)}
                  {formData.geographicRegion.parish && (
                    <span className="ml-2 font-medium">({formData.geographicRegion.parish})</span>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <MilestonesEditor
              projectId={projectId}
              milestones={projectId ? undefined : formData.milestones || []}
              onChange={projectId ? undefined : (milestones) => handleChange('milestones', milestones)}
              disabled={false}
            />
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            {projectId ? (
              <GoalsEditor
                projectId={projectId}
                milestones={formData.milestones || []}
                disabled={false}
              />
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-[var(--color-subtext-color)]">
                  Note: Goals will be stored in a separate collection. You can add them after project creation.
                </p>
                <GoalsEditor
                  milestones={formData.milestones || []}
                  goals={formData.goals || []}
                  onGoalsChange={(goals) => handleChange('goals', goals)}
                  disabled={false}
                />
              </div>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            {projectId ? (
              <ProjectFilesManager
                projectId={projectId}
                files={projectFiles}
                onFilesChange={async () => {
                  // Refresh files from API
                  if (accessToken) {
                    try {
                      const files = await api.projectFiles.getProjectFiles(projectId, false, accessToken);
                      setProjectFiles(files);
                    } catch (error) {
                      console.error('Error refreshing project files:', error);
                    }
                  }
                }}
              />
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-[var(--color-subtext-color)]">
                  Note: Files uploaded here will be moved to the project directory after project creation.
                </p>
                <FileUpload
                  label="Project Files"
                  onFileSelect={(files) => handleChange('files', files)}
                  multiple
                  accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.zip"
                  maxSize={104857600}
                  autoUpload={true}
                  projectId={projectId}
                  allowDocumentMetadata={true}
                />
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-[var(--color-default-background)] p-6 rounded-lg shadow-sm">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-[var(--color-default-font)]">
            Step {step} of 5
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all"
            style={{ width: `${(step / 5) * 100}%` }}
          />
        </div>
      </div>

      {renderStep()}

      <div className="flex justify-between mt-6">
        <Button
          type="button"
          variant="outline"
          onClick={() => (step > 1 ? setStep(step - 1) : navigate('/projects'))}
        >
          {step === 1 ? 'Cancel' : 'Previous'}
        </Button>
        <Button type="submit" isLoading={isSubmitting}>
          {step === 5 ? 'Submit' : 'Next'}
        </Button>
      </div>
    </form>
  );
};

export default ProjectForm;

